import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  CircleStackIcon,
  ArrowsRightLeftIcon,
  BoltIcon,
  CheckBadgeIcon,
  PlusIcon,
  BuildingOffice2Icon,
  DocumentArrowDownIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  BellIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  ArrowRightCircleIcon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const API_URL = '/api/v1';

/* ─── Admin Stats Card ─────────────────────── */
const StatCard = ({ title, value, sub, icon: Icon, colorClass }) => (
  <div className="glass-card p-6 rounded-none border-gray-200 bg-white">
    <div className="flex items-center justify-between mb-2">
      <div className={`p-2 rounded ${colorClass} bg-opacity-10`}>
        <Icon className={`w-6 h-6 ${colorClass}`} />
      </div>
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.1em]">{i18n.t('hash_verified')}</span>
    </div>
    <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{title}</p>
    <div className="flex items-baseline gap-2">
      <h3 className="text-2xl font-black text-gray-900 tracking-tight">{value}</h3>
      <span className="text-[10px] text-gray-400 font-bold uppercase">{sub}</span>
    </div>
  </div>
);

/* ─── Admin Dashboard ──────────────────────── */
function AdminDashboard() {
  const [stats, setStats] = useState({ mwh_issued: 0, active_plants: 0, participants: 0 });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/certificates/summary/stats`),
      axios.get(`${API_URL}/audit?limit=8`)
    ]).then(([s, a]) => {
      setStats(s.data);
      setActivity(a.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-40 text-center text-gray-400 font-black uppercase tracking-[0.3em] text-xs transition-opacity animate-pulse">{i18n.t('sov_node')}</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-black text-gray-800 tracking-tighter uppercase">{i18n.t('audit')}</h1>
          <p className="text-[10px] text-gray-400 translate-y-[-2px] font-black uppercase tracking-[0.2em]">{i18n.t('subtitle')}</p>
        </div>
        <button className="bg-[#155e75] text-white text-[11px] font-black uppercase tracking-widest px-8 py-3 shadow-xl hover:bg-[#0e4e61] transition-all">
          {i18n.t('gen_report')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title={i18n.t('nat_volume')} value={stats.mwh_issued.toLocaleString()} sub="MWh" icon={CircleStackIcon} colorClass="text-blue-600" />
        <StatCard title={i18n.t('ent_reg')} value={stats.participants} sub="Participants" icon={UserGroupIcon} colorClass="text-cyan-600" />
        <StatCard title={i18n.t('qual_assets')} value={stats.active_plants} sub={i18n.t('plants')} icon={ChartBarIcon} colorClass="text-[#155e75]" />
        <StatCard title={i18n.t('hub_status')} value="100" sub="%" icon={BellIcon} colorClass="text-emerald-600" />
      </div>

      <div className="glass-card rounded-none border-gray-200 bg-white shadow-xl overflow-hidden mt-8">
        <div className="p-5 border-b border-gray-100 font-black text-gray-700 text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 bg-gray-50/50">
          <ClockIcon className="w-5 h-5 text-[#155e75]" /> {i18n.t('audit_fingerprint')}
        </div>
        <div className="overflow-x-auto">
          <table className="ent-table text-[11px]">
            <thead>
              <tr className="bg-gray-50/20 text-gray-400 uppercase font-black tracking-widest text-[9px]">
                <th>{i18n.t('action_col')}</th>
                <th>{i18n.t('entity_col')}</th>
                <th>{i18n.t('time_col')}</th>
                <th className="text-right">{i18n.t('integrity_col')}</th>
              </tr>
            </thead>
            <tbody>
              {activity.map(log => (
                <tr key={log.id} className="hover:bg-blue-50/30 transition-colors">
                  <td><span className={`px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase border ${log.status === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{log.action}</span></td>
                  <td className="text-gray-500 font-mono text-[10px] font-bold">{log.entity_id.substring(0, 15)}...</td>
                  <td className="text-gray-400 font-medium tabular-nums">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="text-right"><span className="text-[9px] text-[#155e75] font-black uppercase tracking-[0.1em]">{i18n.t('hash_verified')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Grid Operator / VVB Dashboard ──────────────── */
function GridOperatorDashboard() {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const role = user?.role || localStorage.getItem('role') || 'GRID_OPERATOR';
  const isVVB = role === 'VVB_AUDITOR';

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/devices`)
      .then(r => setDevices(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [role]);

  if (loading) return <div className="py-40 text-center text-gray-400 font-black uppercase tracking-[0.3em] text-xs animate-pulse">{i18n.t('sov_node')}</div>;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-gray-800 tracking-tighter uppercase">
            {isVVB ? 'Independent Auditor (VVB)' : i18n.t('grid_operator')}
          </h1>
          <p className="text-[10px] text-indigo-600 font-black uppercase tracking-[0.2em]">
            {isVVB ? 'Accredited Verification Node: ISO 14065' : i18n.t('grid_operator_verif')}
          </p>
        </div>
        <Link to="/grid/metering" className="bg-indigo-600 text-white px-8 py-4 rounded-sm text-[11px] font-black uppercase tracking-widest shadow-2xl hover:bg-indigo-700 transition-all flex items-center gap-2">
          <TableCellsIcon className="w-5 h-5" /> {i18n.t('metering_portal')}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card p-8 bg-white border border-gray-200 text-center shadow-xl border-t-4 border-t-indigo-600">
          <BoltIcon className="w-10 h-10 text-indigo-600 mx-auto mb-4" />
          <p className="text-4xl font-black text-gray-900">{devices.length}</p>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-2">{i18n.t('plants')}</p>
        </div>
        <div className="glass-card p-8 bg-white border border-gray-200 text-center shadow-xl border-t-4 border-t-amber-500">
          <ClockIcon className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <p className="text-4xl font-black text-gray-900">{devices.length}</p>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-2">{i18n.t('readings_pending')}</p>
        </div>
        <div className="glass-card p-8 bg-white border border-gray-200 text-center shadow-xl border-t-4 border-t-emerald-600">
          <ShieldCheckIcon className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
          <p className="text-4xl font-black text-gray-900">98.2%</p>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-2">{i18n.t('compliance_score')}</p>
        </div>
      </div>

      <div className="glass-card bg-white border border-gray-200 shadow-sm rounded-none overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-indigo-600" /> {i18n.t('grid_tied_assets')}
          </h3>
          <Link to="/grid/metering" className="text-[10px] font-black text-indigo-600 hover:underline uppercase">{i18n.t('manage_all')} →</Link>
        </div>
        <div className="overflow-x-auto text-[11px]">
          <table className="ent-table">
            <thead>
              <tr className="bg-gray-50/20 text-[9px] uppercase font-black">
                <th>{i18n.t('asset_name')}</th>
                <th>{i18n.t('law_flag')}</th>
                <th>{i18n.t('city_col')}</th>
                <th className="text-right">{i18n.t('actions_col')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {devices.slice(0, 5).map(d => (
                <tr key={d.id} className="hover:bg-indigo-50/30 transition-colors">
                  <td className="font-bold text-gray-800">{d.device_name}</td>
                  <td>
                    <div className="flex gap-2">
                      {d.is_autoproduction && <span className="px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase bg-amber-50 border border-amber-200 text-amber-700">{i18n.t('law_82_21')}</span>}
                      {!d.is_autoproduction && <span className="px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase bg-blue-50 border border-blue-200 text-blue-700">GRID-TIED</span>}
                    </div>
                  </td>
                  <td className="text-gray-400 font-bold uppercase">{d.city || 'NATIONAL'}</td>
                  <td className="text-right">
                    <Link to="/grid/metering" className="text-indigo-600 font-black uppercase text-[10px] hover:underline flex items-center gap-1 justify-end">
                      {i18n.t('verify_readings')} <ArrowRightCircleIcon className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Participant Dashboard ─────────────────── */
function ParticipantDashboard() {
  const [certs, setCerts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/certificates`),
      axios.get(`${API_URL}/transfers`),
      axios.get(`${API_URL}/devices`),
    ]).then(([c, t, d]) => {
      setCerts(c.data);
      setTransfers(t.data);
      setDevices(d.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-40 text-center text-gray-400 font-black uppercase tracking-[0.3em] text-xs">{i18n.t('decrypting_wallet')}</div>;

  const activeCerts = certs.filter(c => c.status === 'ISSUED');
  const pendingCerts = certs.filter(c => c.status === 'PENDING_ISSUANCE');
  const totalMwh = activeCerts.reduce((s, c) => s + (parseFloat(c.output_mwh) || 0), 0);

  return (
    <div className="space-y-8 animate-fade-in group">
      <div className="flex items-center justify-between border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-gray-800 tracking-tighter uppercase">{i18n.t('dashboard')}</h1>
          <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em]">{i18n.t('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/issuance/request" className="bg-[#155e75] text-white px-8 py-3 rounded-sm text-[11px] font-black uppercase tracking-widest shadow-2xl hover:bg-[#0e4e61] transition-all">
            {i18n.t('register')} Assets
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-6 bg-white border border-gray-200 text-center group-hover:shadow-xl transition-shadow">
          <CircleStackIcon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <p className="text-3xl font-black text-gray-900">{activeCerts.length}</p>
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-1">{i18n.t('certificates')}</p>
        </div>
        <div className="glass-card p-6 bg-white border border-gray-200 text-center group-hover:shadow-xl transition-shadow">
          <BoltIcon className="w-8 h-8 text-[#155e75] mx-auto mb-3" />
          <p className="text-3xl font-black text-gray-900">{totalMwh.toLocaleString()}</p>
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-1">{i18n.t('total_mwh_portfolio')}</p>
        </div>
        <div className="glass-card p-6 bg-white border border-gray-200 text-center group-hover:shadow-xl transition-shadow">
          <ClockIcon className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="text-3xl font-black text-gray-900">{pendingCerts.length}</p>
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-1">{i18n.t('status_pending_issuance')}</p>
        </div>
        <div className="glass-card p-6 bg-white border border-gray-200 text-center group-hover:shadow-xl transition-shadow">
          <BuildingOffice2Icon className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
          <p className="text-3xl font-black text-gray-900">{devices.length}</p>
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-1">{i18n.t('plants')}</p>
        </div>
      </div>

      {/* Quick Actions Integration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/transfers/new" className="glass-card p-8 bg-indigo-50 border border-indigo-100 flex items-center gap-6 hover:bg-indigo-100 transition-all border-l-4 border-l-indigo-600 shadow-md">
          <ArrowsRightLeftIcon className="w-10 h-10 text-indigo-600" />
          <div>
            <p className="text-sm font-black text-indigo-900 uppercase leading-tight">Transfer GOs</p>
            <p className="text-[9px] font-bold text-indigo-700/60 uppercase mt-1">Inter-Registry Exchange</p>
          </div>
        </Link>
        <Link to="/certificates" className="glass-card p-8 bg-emerald-50 border border-emerald-100 flex items-center gap-6 hover:bg-emerald-100 transition-all border-l-4 border-l-emerald-600 shadow-md">
          <CheckBadgeIcon className="w-10 h-10 text-emerald-600" />
          <div>
            <p className="text-sm font-black text-emerald-900 uppercase leading-tight">Disclosure</p>
            <p className="text-[9px] font-bold text-emerald-700/60 uppercase mt-1">Final Cancellation (Annex 7)</p>
          </div>
        </Link>
        <Link to="/reports" className="glass-card p-8 bg-slate-50 border border-slate-100 flex items-center gap-6 hover:bg-slate-100 transition-all border-l-4 border-l-slate-600 shadow-md">
          <DocumentArrowDownIcon className="w-10 h-10 text-slate-600" />
          <div>
            <p className="text-sm font-black text-slate-900 uppercase leading-tight">{i18n.t('compliance')}</p>
            <p className="text-[9px] font-bold text-slate-700/60 uppercase mt-1">{i18n.t('red_ii_export')}</p>
          </div>
        </Link>
      </div>

      <div className="glass-card bg-white border border-gray-200 shadow-xl overflow-hidden mt-10">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
            <CircleStackIcon className="w-5 h-5 text-indigo-600" /> Recent Certificate Logs
          </h3>
          <Link to="/certificates" className="text-[10px] font-black text-[#155e75] hover:underline uppercase tracking-widest">View Full Ledger →</Link>
        </div>
        <div className="overflow-x-auto text-[11px]">
          <table className="ent-table">
            <thead>
              <tr className="bg-gray-50/20 text-[9px] uppercase font-black tracking-widest text-gray-400">
                <th>{i18n.t('gs1_col')}</th>
                <th>{i18n.t('status_col')}</th>
                <th>{i18n.t('volume_col')}</th>
                <th className="text-right">{i18n.t('issuance_col')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {certs.slice(0, 5).map(c => (
                <tr key={c.certificate_id} className="hover:bg-blue-50/40 transition-colors">
                  <td className="font-mono font-bold text-gray-700">{c.gs1_id}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase border ${c.status === 'ISSUED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {i18n.t(`status_${c.status.toLowerCase()}`)}
                    </span>
                  </td>
                  <td className="font-black text-gray-700">{c.output_mwh} MWh</td>
                  <td className="text-right text-gray-400 font-medium">{new Date(c.issue_date).toLocaleDateString()}</td>
                </tr>
              ))}
              {certs.length === 0 && (
                <tr><td colSpan={4} className="text-center py-20 text-gray-400 font-black uppercase text-xs">{i18n.t('wallet_empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Export ──────────────────────────── */
export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || localStorage.getItem('role') || 'PRODUCER';

  if (role === 'ADMIN' || role === 'ISSUING_BODY') return <AdminDashboard />;
  if (role === 'GRID_OPERATOR' || role === 'VVB_AUDITOR') return <GridOperatorDashboard />;
  return <ParticipantDashboard />;
}
